import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AppHeader from '../components/AppHeader';
import { toast } from 'sonner';
import { Mail, MailOpen, Send, X, Plus, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MessagesPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('inbox');
  const [composeForm, setComposeForm] = useState({
    receiver_id: '',
    subject: '',
    content: ''
  });
  const [directCompanyName, setDirectCompanyName] = useState('');

  useEffect(() => {
    fetchMessages();
    fetchCompanies();
  }, []);

  useEffect(() => {
    const companyId = searchParams.get('to');
    const companyName = searchParams.get('name');
    if (companyId) {
      setShowCompose(true);
      setDirectCompanyName(companyName || '');
      setComposeForm(prev => ({
        ...prev,
        receiver_id: companyId,
        subject: companyName ? (language === 'ar' ? `استفسار بخصوص ${companyName}` : `Inquiry about ${companyName}`) : ''
      }));
    }
  }, [searchParams, language]);

  const fetchMessages = async () => {
    try {
      const { data } = await axios.get(`${API}/messages`);
      setMessages(Array.isArray(data) ? data : data.items || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const { data } = await axios.get(`${API}/companies/list`);
      setCompanies(data.filter(c => c.user_id !== user?.id));
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const openCompose = async () => {
    try {
      const { data: adminInfo } = await axios.get(`${API}/admin/info`);
      setComposeForm({ receiver_id: adminInfo.id, subject: '', content: '' });
      setDirectCompanyName(adminInfo.name);
      setShowCompose(true);
    } catch (error) {
      console.error('Error fetching admin:', error);
      setShowCompose(true);
    }
  };

  const handleSend = async () => {
    if (!composeForm.receiver_id || !composeForm.subject || !composeForm.content) {
      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول' : 'Please fill all fields');
      return;
    }
    try {
      await axios.post(`${API}/messages`, composeForm);
      toast.success(language === 'ar' ? 'تم إرسال الرسالة بنجاح' : 'Message sent successfully');
      setShowCompose(false);
      setDirectCompanyName('');
      setComposeForm({ receiver_id: '', subject: '', content: '' });
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(language === 'ar' ? 'فشل إرسال الرسالة' : 'Failed to send message');
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.patch(`${API}/messages/${messageId}/read`, {});
      setMessages(messages.map(msg =>
        msg.id === messageId ? { ...msg, read: true } : msg
      ));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const sentMessages = messages.filter(m => m.sender_id === user?.id);
  const receivedMessages = messages.filter(m => m.receiver_id === user?.id);
  const unreadCount = receivedMessages.filter(m => !m.read).length;

  return (
    <div className="min-h-screen bg-background" data-testid="messages-page">
      <AppHeader />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-4xl font-heading font-bold text-primary mb-1 sm:mb-2">{t('messages')}</h1>
            <p className="text-sm sm:text-base text-muted-foreground">{t('communicateWithCompanies')}</p>
          </div>
          <button
            onClick={openCompose}
            data-testid="compose-message-btn"
            className="flex items-center justify-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-accent text-white rounded-lg hover:bg-accent/90 font-semibold text-sm transition-all shadow-sm hover:shadow-md w-full sm:w-auto"
          >
            <Plus size={18} />
            {language === 'ar' ? 'رسالة جديدة' : 'New Message'}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-border">
          <button
            onClick={() => setActiveTab('inbox')}
            data-testid="tab-inbox"
            className={`pb-3 px-4 font-medium transition-colors relative ${
              activeTab === 'inbox' ? 'text-accent border-b-2 border-accent -mb-px' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('inboxMessages')} {unreadCount > 0 && <span className="ml-1 px-2 py-0.5 text-xs bg-accent text-white rounded-full">{unreadCount}</span>}
          </button>
          <button
            onClick={() => setActiveTab('sent')}
            data-testid="tab-sent"
            className={`pb-3 px-4 font-medium transition-colors relative ${
              activeTab === 'sent' ? 'text-accent border-b-2 border-accent -mb-px' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('sentMessages')}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="messages-loading">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div>
            {/* Inbox */}
            {activeTab === 'inbox' && (
              receivedMessages.length === 0 ? (
                <div className="bg-white p-16 border border-border rounded-xl text-center" data-testid="no-received-messages">
                  <Mail size={56} className="mx-auto mb-4 text-muted-foreground/40" />
                  <p className="text-muted-foreground">{t('noInboxMessages')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {receivedMessages.map((message) => (
                    <div
                      key={message.id}
                      className={`bg-white p-5 border rounded-xl cursor-pointer hover:shadow-sm transition-all ${
                        !message.read ? 'border-accent bg-accent/5' : 'border-border'
                      }`}
                      onClick={() => { setSelectedMessage(message); if (!message.read) markAsRead(message.id); }}
                      data-testid={`received-message-${message.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className={`text-base ${!message.read ? 'font-bold' : 'font-medium'} text-primary`}>{message.subject}</h3>
                            {!message.read && <span className="px-2 py-0.5 text-xs bg-accent text-white rounded-full">{t('newMessage')}</span>}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {language === 'ar' ? 'من' : 'From'}: <span className="font-medium text-foreground">{message.sender_name || message.sender_id}</span>
                          </p>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{message.content}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(message.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')}</span>
                          <ChevronRight size={16} className="text-muted-foreground" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}

            {/* Sent */}
            {activeTab === 'sent' && (
              sentMessages.length === 0 ? (
                <div className="bg-white p-16 border border-border rounded-xl text-center" data-testid="no-sent-messages">
                  <MailOpen size={56} className="mx-auto mb-4 text-muted-foreground/40" />
                  <p className="text-muted-foreground">{t('noSentMessages')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sentMessages.map((message) => (
                    <div
                      key={message.id}
                      className="bg-white p-5 border border-border rounded-xl cursor-pointer hover:shadow-sm transition-all"
                      onClick={() => setSelectedMessage(message)}
                      data-testid={`sent-message-${message.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="text-base font-medium text-primary mb-1">{message.subject}</h3>
                          <p className="text-sm text-muted-foreground">
                            {language === 'ar' ? 'إلى' : 'To'}: <span className="font-medium text-foreground">{message.receiver_name || message.receiver_id}</span>
                          </p>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{message.content}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(message.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')}</span>
                          <ChevronRight size={16} className="text-muted-foreground" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedMessage(null)} data-testid="message-detail-overlay">
          <div className="bg-white rounded-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="message-detail-modal">
            <div className="p-6 border-b border-border">
              <div className="flex items-start justify-between">
                <h2 className="text-xl font-heading font-bold text-primary">{selectedMessage.subject}</h2>
                <button onClick={() => setSelectedMessage(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X size={18} className="text-muted-foreground" />
                </button>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {selectedMessage.sender_id === user?.id
                  ? `${language === 'ar' ? 'إلى' : 'To'}: ${selectedMessage.receiver_name || selectedMessage.receiver_id}`
                  : `${language === 'ar' ? 'من' : 'From'}: ${selectedMessage.sender_name || selectedMessage.sender_id}`
                }
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {new Date(selectedMessage.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            <div className="p-6">
              <p className="text-foreground leading-relaxed whitespace-pre-wrap">{selectedMessage.content}</p>
            </div>
          </div>
        </div>
      )}

      {/* Compose Modal */}
      {showCompose && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCompose(false)} data-testid="compose-modal-overlay">
          <div className="bg-white rounded-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="compose-modal">
            <div className="p-6 border-b border-border">
              <div className="flex items-start justify-between">
                <h2 className="text-xl font-heading font-bold text-primary">
                  {language === 'ar' ? 'رسالة جديدة' : 'New Message'}
                </h2>
                <button onClick={() => setShowCompose(false)} data-testid="close-compose" className="p-2 hover:bg-gray-100 rounded-lg">
                  <X size={18} className="text-muted-foreground" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-semibold text-muted-foreground mb-1 block">
                  {language === 'ar' ? 'إلى (الشركة)' : 'To (Company)'}
                </label>
                {directCompanyName ? (
                  <div className="w-full px-4 py-3 border border-border rounded-lg bg-gray-50 text-foreground font-medium" data-testid="compose-receiver-fixed">
                    {directCompanyName}
                  </div>
                ) : (
                  <select
                    value={composeForm.receiver_id}
                    onChange={(e) => setComposeForm(prev => ({ ...prev, receiver_id: e.target.value }))}
                    data-testid="compose-receiver-select"
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent bg-white"
                  >
                    <option value="">{language === 'ar' ? 'اختر شركة...' : 'Select a company...'}</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="text-sm font-semibold text-muted-foreground mb-1 block">
                  {language === 'ar' ? 'الموضوع' : 'Subject'}
                </label>
                <input
                  type="text"
                  value={composeForm.subject}
                  onChange={(e) => setComposeForm(prev => ({ ...prev, subject: e.target.value }))}
                  data-testid="compose-subject-input"
                  className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder={language === 'ar' ? 'موضوع الرسالة' : 'Message subject'}
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-muted-foreground mb-1 block">
                  {language === 'ar' ? 'محتوى الرسالة' : 'Message'}
                </label>
                <textarea
                  value={composeForm.content}
                  onChange={(e) => setComposeForm(prev => ({ ...prev, content: e.target.value }))}
                  data-testid="compose-content-input"
                  rows={5}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder={language === 'ar' ? 'اكتب رسالتك هنا...' : 'Write your message here...'}
                />
              </div>
              <button
                onClick={handleSend}
                data-testid="send-message-btn"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent text-white rounded-lg hover:bg-accent/90 font-semibold transition-all"
              >
                <Send size={18} />
                {language === 'ar' ? 'إرسال' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
